/** Assistant / tool visibility in Fleet Control chat transcript. */
export type ChatAssistDisplay = "off" | "on" | "minimal";

export const DEFAULT_CHAT_ASSIST_DISPLAY: ChatAssistDisplay = "on";

export function isChatAssistDisplay(value: unknown): value is ChatAssistDisplay {
  return value === "off" || value === "on" || value === "minimal";
}

/** Migrate legacy boolean `chatShowThinking` from localStorage. */
export function migrateChatAssistDisplay(parsed: {
  chatAssistDisplay?: unknown;
  chatShowThinking?: unknown;
}): ChatAssistDisplay {
  if (isChatAssistDisplay(parsed.chatAssistDisplay)) {
    return parsed.chatAssistDisplay;
  }
  if (typeof parsed.chatShowThinking === "boolean") {
    return parsed.chatShowThinking ? "on" : "off";
  }
  return DEFAULT_CHAT_ASSIST_DISPLAY;
}

export function cycleChatAssistDisplay(current: ChatAssistDisplay): ChatAssistDisplay {
  switch (current) {
    case "off":
      return "on";
    case "on":
      return "minimal";
    case "minimal":
      return "off";
    default:
      return DEFAULT_CHAT_ASSIST_DISPLAY;
  }
}

export function showThinkingFromAssistDisplay(mode: ChatAssistDisplay): boolean {
  return mode === "on";
}

export function resolveAssistDisplay(props: {
  assistDisplay?: ChatAssistDisplay;
  showThinking: boolean;
}): ChatAssistDisplay {
  if (props.assistDisplay) {
    return props.assistDisplay;
  }
  return props.showThinking ? "on" : "off";
}

import {
  isOperatorFacingTranscriptMessage,
  isOperatorMilestoneInjectText,
} from "../../../../src/chat/transcript-filters.js";
import type { ChatSubagentCard } from "../ui-types.ts";
import { isFileChangesMessage } from "./chat-file-changes-summary.ts";
import { extractText } from "./message-extract.ts";
import { isPlanApprovalMessage } from "./plan-approval-notice.ts";
import { filterSubagentRunCards } from "./subagent-run-display.ts";
import { fleetSummaryHeadline, summarizeSubagentFleet } from "./subagent-run-status.ts";

const FLEET_COMPLETE_LABEL = "fleet-complete";

export { isFileChangesMessage };

/** Matches fleetSummaryHeadline() for any settled fleet size (not just 1–2). */
const FLEET_COMPLETION_HEADLINE_RE =
  /\*\*All(?:\s+\d+)?\s+tasks\s+complete(?:\s*\([^)]*\))?\*\*|\*\*All(?:\s+\d+)?\s+subagents\s+idle\*\*|\*\*All tasks stopped — \d+ failed\*\*|\*\*All tasks idle\*\*|\*\*All tasks complete\*\*/i;

/**
 * Labels that mark an injected chat message as a fleet/plan completion
 * notice. The chat.inject label is always `fleet-complete` on the gateway
 * side, but the message body itself can be prefixed with `[fleet-complete]`
 * (short legacy form) or `[synthesis]` (the operator-trust-loop plan
 * synthesis written by `requestFleetPlanOperatorSynthesis`). Both forms
 * describe the same kind of message and must share the same UI rendering
 * — otherwise `[synthesis]` falls through to a plain text bubble and
 * reads as raw operator-facing text instead of a polished completion card.
 */
const FLEET_COMPLETION_BODY_LABELS = ["fleet-complete", "synthesis"] as const;

function bodyHasFleetCompletionLabel(text: string): boolean {
  for (const label of FLEET_COMPLETION_BODY_LABELS) {
    if (text.includes(`[${label}]`)) {
      return true;
    }
  }
  return false;
}

/** Injected fleet completion notices use chat.inject label prefix. */
export function isFleetCompletionMessage(message: unknown): boolean {
  const text = extractText(message) ?? "";
  if (bodyHasFleetCompletionLabel(text)) {
    return true;
  }
  const m = message as Record<string, unknown>;
  const stopReason = typeof m.stopReason === "string" ? m.stopReason.toLowerCase() : "";
  if (stopReason !== "injected") {
    return false;
  }
  if (FLEET_COMPLETION_HEADLINE_RE.test(text)) {
    return true;
  }
  // Legacy fallback for older notices without markdown bold.
  const headlines = [
    fleetSummaryHeadline({
      total: 1,
      ongoing: 0,
      completed: 1,
      failed: 0,
      defunct: 0,
      cancelled: 0,
      idle: 0,
      overall: "all_complete",
    }),
    fleetSummaryHeadline({
      total: 17,
      ongoing: 0,
      completed: 17,
      failed: 0,
      defunct: 0,
      cancelled: 0,
      idle: 0,
      overall: "all_complete",
    }),
    fleetSummaryHeadline({
      total: 1,
      ongoing: 0,
      completed: 0,
      failed: 1,
      defunct: 0,
      cancelled: 0,
      idle: 0,
      overall: "needs_attention",
    }),
  ];
  const stripped = text.replace(/^\[[^\]]+\]\s*/i, "").trim();
  return headlines.some((h) => stripped.includes(h) || text.includes(`**${h}**`));
}

export function shouldIncludeMessageInMinimalAssist(message: unknown): boolean {
  const m = message as Record<string, unknown>;
  const role = typeof m.role === "string" ? m.role.toLowerCase() : "";
  if (role === "user") {
    const text = extractText(message) ?? "";
    if (text.startsWith("A new session was started via /new or /reset")) {
      return false;
    }
    return true;
  }
  if (isFleetCompletionMessage(message)) {
    return true;
  }
  // The plan approval is the operator's answer to "did my plan start?" —
  // dropping it in minimal mode leaves the run looking like it never
  // launched.
  if (isPlanApprovalMessage(message)) {
    return true;
  }
  const text = extractText(message) ?? "";
  if (isOperatorMilestoneInjectText(text)) {
    return false;
  }
  if (isOperatorFacingTranscriptMessage(message)) {
    return true;
  }
  if (isFileChangesMessage(message)) {
    return true;
  }
  return false;
}

export function buildMinimalAssistStatusLabel(params: {
  runActive: boolean;
  subagents: ChatSubagentCard[];
  liveRunHint?: string | null;
}): string | null {
  if (!params.runActive) {
    return null;
  }
  const runCards = filterSubagentRunCards(params.subagents);
  if (runCards.length > 0) {
    const summary = summarizeSubagentFleet(runCards);
    return fleetSummaryHeadline(summary);
  }
  const hint = params.liveRunHint?.trim();
  return hint || "Working…";
}

export function titleForChatAssistDisplay(mode: ChatAssistDisplay): string {
  switch (mode) {
    case "off":
      return "Hide assistant thinking and tool output (click to show full transcript)";
    case "on":
      return "Show full assistant, tool, and subagent output (click for distraction-free mode)";
    case "minimal":
      return "Distraction-free: your messages and completion only (click to hide assistant output)";
    default:
      return "Toggle assistant display mode";
  }
}
